'use client'

import { Suspense, useEffect } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'
import { getAnalytics } from '@/lib/analytics/client'
import { ConsentBanner } from '@/components/analytics/consent-banner'
import { WebVitals } from '@/components/analytics/web-vitals'

const KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY

/**
 * Mounts the analytics surfaces. It owns NO init/consent logic — getAnalytics() is the single
 * owner of PostHog startup and gates everything on consent, so these children are all no-ops
 * (zero network) until the user accepts.
 */
export function AnalyticsProvider({ children }: { children: React.ReactNode }) {
  if (!KEY) return <>{children}</>
  return (
    <>
      <Suspense fallback={null}><PageviewTracker /></Suspense>
      <WebVitals />
      {children}
      <ConsentBanner />
    </>
  )
}

/** Manual pageview on client navigation (App Router). getAnalytics() ensures init + gates on consent. */
function PageviewTracker() {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  useEffect(() => {
    if (!pathname) return
    getAnalytics().pageview(window.location.origin + pathname)
  }, [pathname, searchParams])
  return null
}
